import { type SetOptional } from 'type-fest';

import { type BaseItem, type NullablePartial } from './common.js';
import type * as PracticePreference from './practice/preference.js';

export enum Kind {
  Inpatient = 'INPATIENT',
  Outpatient = 'OUTPATIENT',
}

export enum BillingType {
  Hospital = 'HOSPITAL',
  Provider = 'PROVIDER',
}

export enum ProviderCategory {
  Specialty = 'SPECIALTY',
  Primary = 'PRIMARY',
  Combined = 'COMBINED',
}

export interface UserPractice {
  practiceId: string;
  userId: string;
}

export interface BasePractice {
  name: string;
  internalName: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  faxNumber: string | null;
  timezone: string;
  longitude: number | null;
  latitude: number | null;
  practiceGroupId: string;
  ehrSystemId: string | null;
  previsitDomain: string | null;
  kind: Kind;
  taxId: string | null;
  isTest: boolean | null;
  isBroadcast: boolean | null;
  billingType: BillingType | null;
  isInternal: boolean | null;
  region: string | null;
  serviceLine: string | null;
  labels: Record<string, string> | null;
  providerCategory: ProviderCategory | null;
  availabilityId: string | null;
}

export interface Practice extends BaseItem, BasePractice {}
export interface PracticeBasic extends BaseItem {
  id: string;
  name: string;
  internalName: string | null;
  timezone: string;
}
export type PracticeFull = Practice & {
  practiceGroupName: string;
  organizationId: string;
  organizationName: string;
};

export interface PracticeWithUserPractice extends PracticeFull {
  userPracticeId: string;
}

export interface CreateValues extends SetOptional<NullablePartial<BasePractice>, 'kind'> {
  ehrSystemId: string;
}
export type UpdateValues = Partial<BasePractice>;

export interface ProviderPractice extends BaseItem {
  providerUserId: string;
  practiceId: string;
}

export interface PracticeAccount extends BaseItem {
  practiceId: string;
  accountId: string;
  livemode: boolean;
}

export interface PracticeCsvImportPreferences extends Pick<
  PracticePreference.Preferences,
  | 'forcePrevisitZipCodeAuth'
  | 'exportPrevisitPatientUploadStatusesEnabled'
  | 'exportPrevisitPatientUploadsEnabled'
  | 'exportResidualUpdatedPrevisitPatientInfoClinicalCommentEnabled'
  | 'exportUpdatedPrevisitFamilyMedicalHistoryEnabled'
  | 'exportUpdatedPrevisitPatientAllergiesEnabled'
  | 'exportUpdatedPrevisitPatientInfoGeneralCommentEnabled'
  | 'exportUpdatedPrevisitPatientInformationEnabled'
  | 'exportUpdatedPrevisitPatientMedicalHistoryEnabled'
  | 'exportUpdatedSocialHistoryEnabled'
> {}

export type PracticeBulkCreateValues = CreateValues & Partial<PracticeCsvImportPreferences>;
export const PRACTICE_IMPORT_COLUMNS: (keyof PracticeBulkCreateValues)[] = [
  'name',
  'internalName',
  'address',
  'city',
  'state',
  'zip',
  'longitude',
  'latitude',
  'phoneNumber',
  'timezone',
  'practiceGroupId',
  'ehrSystemId',
  'previsitDomain',
  'kind',
  'isTest',
  'billingType',
  'isInternal',
  'forcePrevisitZipCodeAuth',
  'exportPrevisitPatientUploadStatusesEnabled',
  'exportPrevisitPatientUploadsEnabled',
  'exportResidualUpdatedPrevisitPatientInfoClinicalCommentEnabled',
  'exportUpdatedPrevisitFamilyMedicalHistoryEnabled',
  'exportUpdatedPrevisitPatientAllergiesEnabled',
  'exportUpdatedPrevisitPatientInfoGeneralCommentEnabled',
  'exportUpdatedPrevisitPatientInformationEnabled',
  'exportUpdatedPrevisitPatientMedicalHistoryEnabled',
  'exportUpdatedSocialHistoryEnabled',
];

export type FindAllOptions = Partial<
  Pick<Practice, 'isBroadcast' | 'name'> & {
    organizationId: string;
    practiceGroupId: string;
    ids: string[];
    excludedIds?: string[];
    practiceGroupIds?: string[];
  }
>;

export type SafePractice = Pick<
  Practice,
  'name' | 'address' | 'city' | 'phoneNumber' | 'faxNumber' | 'state' | 'timezone' | 'zip'
>;

export type SafePracticeWithDistance = SafePractice & {
  distance?: number;
  practiceId: string;
};

// There's a constraint check in the database that allows only the states in this enum
// https://github.com/VivaaHealth/vivaa/pull/13368
export enum PracticeState {
  AK = 'AK',
  AL = 'AL',
  AR = 'AR',
  AS = 'AS',
  AZ = 'AZ',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DC = 'DC',
  DE = 'DE',
  FL = 'FL',
  FM = 'FM',
  GA = 'GA',
  GU = 'GU',
  HI = 'HI',
  IA = 'IA',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  KS = 'KS',
  KY = 'KY',
  LA = 'LA',
  MA = 'MA',
  MD = 'MD',
  ME = 'ME',
  MH = 'MH',
  MI = 'MI',
  MN = 'MN',
  MO = 'MO',
  MP = 'MP',
  MS = 'MS',
  MT = 'MT',
  NC = 'NC',
  ND = 'ND',
  NE = 'NE',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NV = 'NV',
  NY = 'NY',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PA = 'PA',
  PR = 'PR',
  PW = 'PW',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VA = 'VA',
  VI = 'VI',
  VT = 'VT',
  WA = 'WA',
  WI = 'WI',
  WV = 'WV',
  WY = 'WY',
}

export function getInternalNameOrName(practice: Practice | PracticeBasic): string {
  if (practice.internalName === '') {
    return practice.name;
  }
  return practice.internalName ?? practice.name;
}
